<template>
  <div class="sections-page bg-gray-50 min-h-screen">
    <section
      class="relative w-full h-[550px] md:h-[650px] flex items-center justify-center overflow-hidden bg-img-yonalishlar bg-cover bg-center"
    >
      <div class="relative z-10 max-w-4xl mx-auto px-4 text-center">
        <div class="bg-black/35 backdrop-blur-md rounded-2xl px-6 py-6 md:px-10 md:py-8 border border-white/20 shadow-xl">
          <div class="inline-block mb-4 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full text-sm font-semibold text-white">
            {{ t('sections.badge') }}
          </div>
          <h1 class="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 text-white leading-tight">
            {{ t('sections.title') }}
          </h1>
          <p class="text-base sm:text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
            {{ t('sections.subtitle') }}
          </p>
        </div>
      </div>
    </section>

    <section class="py-12 bg-white/20 backdrop-blur-md border-b border-gray-200 relative z-10 -mt-16">
      <div class="max-w-7xl mx-auto px-4">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div class="text-center">
            <div class="text-4xl md:text-5xl font-black text-indigo-600 mb-2">{{ sections.length }}</div>
            <div class="text-gray-600 font-medium">{{ t('sections.statsPrograms') }}</div>
          </div>
          <div class="text-center">
            <div class="text-4xl md:text-5xl font-black text-indigo-600 mb-2">{{ totalGroups }}</div>
            <div class="text-gray-600 font-medium">{{ t('sections.statsGroups') }}</div>
          </div>
          <div class="text-center">
            <div class="text-4xl md:text-5xl font-black text-indigo-600 mb-2">1000+</div>
            <div class="text-gray-600 font-medium">{{ t('hero.students') }}</div>
          </div>
          <div class="text-center">
            <div class="text-4xl md:text-5xl font-black text-indigo-600 mb-2">95%</div>
            <div class="text-gray-600 font-medium">{{ t('sections.statsEmployment') }}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="py-16 md:py-24 bg-gradient-to-br from-slate-50 to-slate-100 relative overflow-hidden">
      <div class="absolute inset-0 opacity-5">
        <div class="absolute inset-0" style="background-image: radial-gradient(circle at 2px 2px, #6366f1 1px, transparent 0); background-size: 40px 40px"></div>
      </div>

      <div class="max-w-5xl mx-auto px-4 relative z-10">
        <div class="text-center mb-12">
          <h2 class="text-4xl md:text-5xl font-black mb-4 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
            {{ t('sections.allPrograms') }}
          </h2>
          <p class="text-gray-600 text-lg">{{ t('sections.choose') }}</p>
        </div>

        <div v-if="loading" class="text-center text-gray-700">{{ t('common.loading') }}</div>

        <div v-else-if="error" class="text-center">
          <p class="text-red-600 mb-4">{{ error }}</p>
          <button @click="fetchSections" class="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">{{ t('common.retry') }}</button>
        </div>

        <div v-else class="space-y-6">
          <div
            v-for="(section, index) in sections"
            :key="section._id || index"
            @click="toggleSection(index)"
            class="group bg-white/60 backdrop-blur-xl rounded-3xl shadow-xl border border-white/50 overflow-hidden transition-all duration-500 cursor-pointer"
            :class="{ 'ring-4 ring-indigo-500/50 scale-[1.02]': activeCard === index }"
          >
            <div class="p-6 md:p-8 flex justify-between items-center">
              <div>
                <h3 class="text-xl md:text-2xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                  {{ section.name }}
                </h3>
                <p class="text-sm text-gray-500 mt-1">{{ t('sections.groupCount', { count: section.groups.length }) }}</p>
              </div>
              <div class="flex-shrink-0">
                <div class="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center transition-all duration-500" :class="{ 'rotate-180 bg-gradient-to-br from-indigo-500 to-purple-600': activeCard === index }">
                  <svg class="w-6 h-6 transition-colors" :class="activeCard === index ? 'text-white' : 'text-indigo-600'" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </div>

            <transition
              enter-active-class="transition-all duration-500 ease-out"
              enter-from-class="opacity-0 max-h-0"
              enter-to-class="opacity-100 max-h-[1000px]"
              leave-active-class="transition-all duration-300 ease-in"
              leave-from-class="opacity-100 max-h-[1000px]"
              leave-to-class="opacity-0 max-h-0"
            >
              <div v-show="activeCard === index" class="px-6 md:px-8 pb-6 md:pb-8">
                <div class="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6">
                  <div class="flex flex-wrap gap-3">
                    <span
                      v-for="group in section.groups"
                      :key="group"
                      class="px-3 py-2 bg-white border border-indigo-100 rounded-xl text-gray-700"
                    >
                      {{ group }}
                    </span>
                  </div>
                </div>
              </div>
            </transition>
          </div>

          <div v-if="sections.length === 0" class="text-center text-gray-600 py-12">
            {{ t('sections.empty') }}
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import sectionsApi from '@/services/sectionsApi';

export default {
  name: 'Sections',
  setup() {
    const { t } = useI18n();
    const sections = ref([]);
    const activeCard = ref(null);
    const loading = ref(false);
    const error = ref('');

    const totalGroups = computed(() => sections.value.reduce((sum, item) => sum + (item.groups?.length || 0), 0));

    const fetchSections = async () => {
      loading.value = true;
      error.value = '';

      try {
        const res = await sectionsApi.get('/api/sections');
        sections.value = Array.isArray(res.data)
          ? res.data.map((item) => ({
              ...item,
              groups: Array.isArray(item.groups) ? item.groups : [],
            }))
          : [];
      } catch (err) {
        error.value = err.response?.data?.message || t('sections.loadError');
        sections.value = [];
      } finally {
        loading.value = false;
      }
    };

    const toggleSection = (index) => {
      activeCard.value = activeCard.value === index ? null : index;
    };

    onMounted(async () => {
      await fetchSections();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    return {
      t,
      sections,
      activeCard,
      loading,
      error,
      totalGroups,
      fetchSections,
      toggleSection,
    };
  },
};
</script>

<style scoped>
.bg-img-yonalishlar {
  background-image: url('@/assets/yonalishlar1.jpg');
}

.sections-page {
  animation: fadeIn 0.5s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.max-h-0 {
  max-height: 0;
}

.max-h-\[1000px\] {
  max-height: 1000px;
}
</style>
